import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  PoundSterling,
  Zap,
  Wrench,
  ShieldCheck,
  Coffee,
  Building2,
  Dumbbell,
  GraduationCap,
  ShoppingBag,
  Hotel,
} from "lucide-react";
import Nav from "@/components/frostico/Nav";
import Footer from "@/components/frostico/Footer";
import LocationPartnerForm from "@/components/frostico/LocationPartnerForm";

const MACHINE_IMG = "/frostico-machine.png";

const PERKS = [
  {
    icon: PoundSterling,
    title: "15% of every scoop",
    body: "Transparent monthly payouts, direct to your business account. Live dashboard shows every sale.",
  },
  {
    icon: Zap,
    title: "Zero upfront cost",
    body: "We fund, ship and install the machine at no cost to you. No lease. No maintenance bills.",
  },
  {
    icon: Wrench,
    title: "We handle everything",
    body: "Stocking, cleaning, maintenance and 24/7 remote monitoring. Your team doesn’t lift a finger.",
  },
  {
    icon: ShieldCheck,
    title: "Fully insured & compliant",
    body: "PAT tested, allergen-labelled, HACCP-friendly. Public liability cover included.",
  },
];

const VENUES = [
  { icon: Coffee, label: "Cafés" },
  { icon: Dumbbell, label: "Gyms" },
  { icon: Building2, label: "Offices" },
  { icon: GraduationCap, label: "Student Halls" },
  { icon: ShoppingBag, label: "Retail" },
  { icon: Hotel, label: "Hotels" },
];

const STEPS = [
  {
    n: "01",
    title: "Apply in 60 seconds",
    body: "Tell us about your location — footfall, opening hours, floor space.",
  },
  {
    n: "02",
    title: "Site survey",
    body: "Our team visits, agrees the best spot, checks power and confirms fit.",
  },
  {
    n: "03",
    title: "Install & launch",
    body: "We install the Frostico machine, load stock and go live. Takes half a day.",
  },
  {
    n: "04",
    title: "Get paid monthly",
    body: "15% of net revenue lands in your account. Every single month.",
  },
];

const EARNINGS = [
  {
    scale: "Coffee shop",
    daily: 40,
    price: 4.5,
  },
  {
    scale: "Busy gym",
    daily: 70,
    price: 4.5,
  },
  {
    scale: "Student hall",
    daily: 110,
    price: 4.5,
  },
];

function calcMonthly(daily, price) {
  return Math.round(daily * price * 30 * 0.15);
}

export default function HostAMachine() {
  return (
    <div className="relative min-h-screen bg-[#0A0B10] text-white grain font-body overflow-hidden">
      <Nav />

      <main className="relative z-10">
        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-20">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors font-mono-accent text-[11px] mb-10"
            >
              <ArrowLeft size={14} /> Back to Frostico
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 text-[11px] font-mono-accent text-[#5AB0FF] mb-6">
                  <span className="h-px w-10 bg-[#5AB0FF]" />
                  <span>Host a Machine · Dorset &amp; Hampshire</span>
                </div>

                <h1
                  data-testid="host-headline"
                  className="font-display font-bold text-[52px] leading-[0.9] md:text-[88px] lg:text-[104px] tracking-tighter"
                >
                  Turn your floor space
                  <br />
                  into a{" "}
                  <span className="text-[#8B5CF6]">monthly cheque</span>.
                </h1>

                <p className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed">
                  Host a Frostico vending machine at your venue and earn{" "}
                  <span className="font-semibold text-white">
                    15% of every sale
                  </span>{" "}
                  — with zero upfront cost, zero staff training, and zero
                  restocking. We handle everything.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <a
                    href="#apply"
                    data-testid="host-cta-apply"
                    className="group inline-flex items-center justify-between gap-4 rounded-full bg-[#8B5CF6] px-6 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 neon-glow-pink"
                  >
                    Apply to host · 60 seconds
                    <ArrowUpRight
                      size={18}
                      className="transition-transform duration-200 group-hover:rotate-45"
                    />
                  </a>
                  <a
                    href="#earnings"
                    className="group inline-flex items-center justify-between gap-4 rounded-full border border-[#5AB0FF]/60 bg-transparent px-6 py-4 text-sm font-semibold text-[#5AB0FF] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#5AB0FF]/10"
                  >
                    See earnings estimates
                    <ArrowUpRight
                      size={18}
                      className="transition-transform duration-200 group-hover:rotate-45"
                    />
                  </a>
                </div>

                <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl">
                  {[
                    { k: "15%", v: "Profit share" },
                    { k: "£0", v: "Upfront cost" },
                    { k: "24/7", v: "Passive income" },
                  ].map((s) => (
                    <div
                      key={s.k}
                      className="border-l border-white/15 pl-4"
                    >
                      <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                        {s.k}
                      </div>
                      <div className="mt-2 font-mono-accent text-[10px] text-white/60">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 bg-white neon-glow-pink">
                  <img
                    src={MACHINE_IMG}
                    alt="Frostico vending machine"
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0A0B10] via-[#0A0B10]/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-mono-accent text-[10px] text-[#5AB0FF]">
                      Frostico
                    </div>
                    <div className="font-display text-2xl font-bold text-white">
                      Floor footprint: 60 × 60 cm
                    </div>
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 rounded-2xl bg-[#8B5CF6] text-white px-4 py-3 rotate-3 shadow-lg neon-glow-pink">
                  <div className="font-mono-accent text-[10px] opacity-80">
                    You keep
                  </div>
                  <div className="font-display text-2xl font-bold leading-none">
                    15%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PERKS */}
        <section className="py-20 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="flex items-end justify-between gap-8 mb-14 flex-col md:flex-row">
              <div>
                <div className="font-mono-accent text-[11px] text-[#5AB0FF] mb-4">
                  Why host with Frostico
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95] max-w-2xl">
                  All upside. <span className="italic text-white/70">No admin.</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {PERKS.map((p) => {
                const I = p.icon;
                return (
                  <div
                    key={p.title}
                    data-testid={`perk-${p.title.split(" ")[0].toLowerCase()}`}
                    className="rounded-2xl border border-white/10 bg-[#15161C] p-8 hover:border-[#8B5CF6]/40 transition-colors"
                  >
                    <I size={22} className="text-[#8B5CF6] mb-6" />
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-3">
                      {p.title}
                    </h3>
                    <p className="text-white/60 leading-relaxed text-sm">
                      {p.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* IDEAL VENUES */}
        <section className="py-20 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-4">
                <div className="font-mono-accent text-[11px] text-[#8B5CF6] mb-4">
                  Perfect for
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[0.95]">
                  If you have{" "}
                  <span className="italic text-white/70">footfall</span>, we have
                  a machine.
                </h2>
                <p className="mt-6 text-white/60 leading-relaxed max-w-sm">
                  Any high-traffic venue works. We especially love placements
                  where families, students or commuters pass by daily.
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {VENUES.map((v) => {
                    const I = v.icon;
                    return (
                      <div
                        key={v.label}
                        data-testid={`venue-${v.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="rounded-2xl border border-white/10 bg-[#15161C] px-6 py-8 flex items-center gap-4 hover:border-[#5AB0FF]/40 hover:bg-[#5AB0FF]/5 transition-colors"
                      >
                        <I size={22} className="text-[#5AB0FF]" />
                        <span className="font-display text-lg font-bold tracking-tight">
                          {v.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EARNINGS ESTIMATOR */}
        <section id="earnings" className="py-24 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="flex items-end justify-between gap-8 mb-14 flex-col md:flex-row">
              <div>
                <div className="font-mono-accent text-[11px] text-[#5AB0FF] mb-4">
                  What you could earn
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95] max-w-2xl">
                  Real numbers.{" "}
                  <span className="italic text-white/70">Zero effort.</span>
                </h2>
              </div>
              <p className="max-w-md text-white/60 leading-relaxed md:text-right">
                Estimates based on average price £4.50 per cup and typical UK
                footfall. Your actual mileage will vary based on location and
                season.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {EARNINGS.map((e) => (
                <div
                  key={e.scale}
                  data-testid={`earning-${e.scale.toLowerCase().replace(/\s/g, "-")}`}
                  className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#15161C] to-[#181228] p-8"
                >
                  <div className="font-mono-accent text-[11px] text-[#8B5CF6] mb-6">
                    {e.scale}
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-6xl md:text-7xl font-bold tracking-tighter">
                      £{calcMonthly(e.daily, e.price).toLocaleString()}
                    </span>
                    <span className="font-mono-accent text-[11px] text-white/50">
                      /mo
                    </span>
                  </div>
                  <div className="mt-2 font-mono-accent text-[10px] text-white/50">
                    Your 15% share
                  </div>
                  <ul className="mt-8 space-y-2 text-sm text-white/60">
                    <li>~{e.daily} cups/day</li>
                    <li>£{e.price.toFixed(2)} avg. price</li>
                    <li>30 days/month</li>
                  </ul>
                </div>
              ))}
            </div>

            <p className="mt-8 text-center font-mono-accent text-[10px] text-white/40">
              * Illustrative example, not a guaranteed rate. Individual venues
              vary.
            </p>
          </div>
        </section>

        {/* PROCESS */}
        <section className="py-24 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="flex items-end justify-between gap-8 mb-14 flex-col md:flex-row">
              <div>
                <div className="font-mono-accent text-[11px] text-[#8B5CF6] mb-4">
                  The process
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95]">
                  From apply to earning{" "}
                  <br className="hidden md:block" />
                  <span className="italic text-white/70">in 21 days.</span>
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {STEPS.map((s) => (
                <div
                  key={s.n}
                  data-testid={`step-${s.n}`}
                  className="rounded-2xl border border-white/10 bg-[#15161C] p-8 relative overflow-hidden"
                >
                  <div className="font-display text-6xl font-bold tracking-tighter text-white/10 mb-4">
                    {s.n}
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* APPLY FORM */}
        <section id="apply" className="py-24 border-t border-white/10">
          <div className="mx-auto max-w-[1200px] px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="font-mono-accent text-[11px] text-[#8B5CF6] mb-4">
                  Apply · 60 seconds
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95]">
                  Let’s find your{" "}
                  <span className="italic text-white/70">perfect spot</span>.
                </h2>
                <p className="mt-6 text-white/60 leading-relaxed max-w-sm">
                  Fill this in and our partnerships team will be in touch
                  within 48 hours to arrange a site survey. No commitment.
                </p>
                <div className="mt-10 space-y-4 text-sm text-white/70">
                  {[
                    "12-month host agreement",
                    "60 × 60 cm floor space",
                    "Standard 13A socket",
                    "Reasonable access for restocking",
                  ].map((r) => (
                    <div
                      key={r}
                      className="flex items-center gap-3 border-b border-white/10 pb-3"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#5AB0FF]" />
                      {r}
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-7 rounded-3xl border border-white/10 bg-[#15161C] p-8 md:p-10">
                <LocationPartnerForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
